import { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type CurrencyCode = 'AED' | 'USD' | 'GBP' | 'EUR' | 'SAR';

export interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  name: string;
  flag: string;
  /** Rate relative to AED (1 AED = X currency) */
  rate: number;
}

export const CURRENCIES: CurrencyConfig[] = [
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham', flag: '🇦🇪', rate: 1.0 },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', rate: 0.2723 },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', rate: 0.2156 },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', rate: 0.2503 },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', flag: '🇸🇦', rate: 1.0210 },
];

const CURRENCY_MAP = Object.fromEntries(CURRENCIES.map((c) => [c.code, c])) as Record<CurrencyCode, CurrencyConfig>;

interface CurrencyContextValue {
  currency: CurrencyConfig;
  setCurrency: (code: CurrencyCode) => void;
  /** Convert amount from AED (or specified fromCurrency) to selected currency */
  convertAmount: (amount: number, fromCurrency?: CurrencyCode) => number;
  /** Format amount (from AED) as a localized string with currency symbol */
  formatCurrency: (amount: number, fromCurrency?: CurrencyCode) => string;
  /** Just the symbol e.g. "$", "£", "AED" */
  symbol: string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const STORAGE_KEY = 'horizon_currency';

function getInitialCurrency(): CurrencyCode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as CurrencyCode | null;
    if (saved && CURRENCY_MAP[saved]) return saved;
  } catch (_e) {
    // ignore
  }
  return 'AED';
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>(getInitialCurrency);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyCode(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_e) { /* ignore */ }
  }, []);

  const currency = CURRENCY_MAP[currencyCode];

  const convertAmount = useCallback(
    (amount: number, fromCurrency: CurrencyCode = 'AED'): number => {
      // Convert to AED first, then to target
      const inAED = fromCurrency === 'AED' ? amount : amount / CURRENCY_MAP[fromCurrency].rate;
      return inAED * currency.rate;
    },
    [currency]
  );

  const formatCurrency = useCallback(
    (amount: number, fromCurrency: CurrencyCode = 'AED'): string => {
      const converted = convertAmount(amount, fromCurrency);
      const isPrefix = ['USD', 'GBP', 'EUR'].includes(currencyCode);
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(converted));
      return isPrefix ? `${currency.symbol}${formatted}` : `${currency.symbol} ${formatted}`;
    },
    [currency, currencyCode, convertAmount]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, convertAmount, formatCurrency, symbol: currency.symbol }),
    [currency, setCurrency, convertAmount, formatCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
}
